import {
  Modal,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import React, { useEffect, useState } from 'react'
import useDebounce from '../../../../../../hooks/useDebounce'
import { useGetFeeStructureTestQuotationQuery } from '../../../../../../redux/services/coreApi'
import {
  CurrencyDto,
  FeeStructureDto,
} from '../../../../../../types/coreApi-types'
import { handledErrorQuery } from '../../../../../../utils/handledError'
import CurrencyModal from '../../../Broker Service/components/CurrencyModal/CurrencyModal'
import FeeContainer from '../../../Broker Service/components/FeeContainer'
import { PayAndReceive } from '../../../Broker Service/containers/CreateSwap'

interface TestQuotationProps {
  feeStructure: FeeStructureDto
  isOpen: boolean
  onClose: () => void
}

const TestQuotation: React.FC<TestQuotationProps> = ({
  feeStructure,
  isOpen,
  onClose,
}) => {
  const currencyModal = useDisclosure()
  const [currencyMode, setCurrencyMode] = useState<'pay' | 'receive'>('pay')
  const [payCurrency, setPayCurrency] = useState<CurrencyDto | undefined>(
    undefined
  )
  const [receiveCurrency, setReceiveCurrency] = useState<
    CurrencyDto | undefined
  >(undefined)
  const [payAmount, setPayAmount] = useState<number>(Number.NaN)
  const [receiveAmount, setReceiveAmount] = useState<number>(Number.NaN)
  const [lastEditedAmountInput, setLastEditedAmountInput] = useState<
    'in' | 'out'
  >('in')

  const debounceMs = 400
  const _payAmount = useDebounce(payAmount, debounceMs)
  const _receiveAmount = useDebounce(receiveAmount, debounceMs)

  const {
    data: quotationData,
    isFetching: quotationIsFetching,
    error: quotationError,
  } = handledErrorQuery(() =>
    useGetFeeStructureTestQuotationQuery(
      payCurrency && receiveCurrency && feeStructure
        ? {
            feeStructureId: feeStructure.id,
            amountIn:
              lastEditedAmountInput === 'in'
                ? isNaN(_payAmount)
                  ? 0
                  : _payAmount
                : undefined,
            amountOut:
              lastEditedAmountInput === 'out'
                ? isNaN(_receiveAmount)
                  ? 0
                  : _receiveAmount
                : undefined,
            currencyInId: payCurrency.id,
            currencyOutId: receiveCurrency.id,
          }
        : skipToken,
      { refetchOnMountOrArgChange: true }
    )
  )

  const isFetchingPayAmount =
    ((receiveAmount !== _receiveAmount && !isNaN(receiveAmount)) ||
      quotationIsFetching) &&
    lastEditedAmountInput === 'out'
  const isFetchingReceiveAmount =
    ((payAmount !== _payAmount && !isNaN(payAmount)) || quotationIsFetching) &&
    lastEditedAmountInput === 'in'

  useEffect(() => {
    if (quotationData) {
      if (lastEditedAmountInput === 'in') {
        setReceiveAmount(quotationData.amount)
      } else {
        setPayAmount(quotationData.amount)
      }
    }
  }, [quotationData, lastEditedAmountInput])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg={'rgba(0, 0, 0,0.85)'} />
      <ModalContent rounded="14px" p="0px" maxW="600px" marginX="16px">
        <div className="rounded-xl relative">
          <div className="p-5 pb-5 flex items-center justify-between">
            <p className="text-default-base">Test Quotation</p>
            <button
              className="color-text-description hover:bg-brand-light-card dark:hover:bg-brand-dark-card flex justify-center items-center h-[32px] w-[32px] rounded-lg cursor-pointer"
              onClick={onClose}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="p-6 pt-0">
            <p className="text-description text-sm mb-6">
              Test how fees are calculated for this fee structure by entering
              pay and receive currencies with amounts.
            </p>

            <CurrencyModal
              isOpen={currencyModal.isOpen}
              onClose={currencyModal.onClose}
              onClickCurrency={currency => {
                if (currencyMode === 'pay') {
                  setPayCurrency(currency)
                }
                if (currencyMode === 'receive') {
                  setReceiveCurrency(currency)
                }
              }}
            />

            <PayAndReceive
              payAmount={payAmount}
              setPayAmount={v => {
                setPayAmount(v)
                if (lastEditedAmountInput !== 'in')
                  setLastEditedAmountInput('in')
              }}
              receiveAmount={receiveAmount}
              setReceiveAmount={v => {
                setReceiveAmount(v)
                if (lastEditedAmountInput !== 'out')
                  setLastEditedAmountInput('out')
              }}
              payCurrency={payCurrency}
              receiveCurrency={receiveCurrency}
              currencyModal={currencyModal}
              isFetchingPayAmount={isFetchingPayAmount}
              isFetchingReceiveAmount={isFetchingReceiveAmount}
              quotationError={quotationError}
              setCurrencyMode={setCurrencyMode}
            />

            {payCurrency && receiveCurrency && (
              <FeeContainer
                payCurrency={payCurrency}
                exchangeRate={quotationData?.rate}
                receiveCurrency={receiveCurrency}
                baseFeeRate={quotationData?.baseFee || 0}
                bankFeeRate={quotationData?.bankFee || 0}
                payAmount={payAmount || 0}
                quotationIsFetching={
                  isFetchingPayAmount || isFetchingReceiveAmount
                }
              />
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}

export default TestQuotation
